import * as R from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, FormControl, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { assessmentManagerHomeRoute } from 'App/common/routeLookup';
import FlexCenter from 'App/components/styles/FlexCenter';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner/TherigyLoadingSpinner';
import { cloneAssessment, getAllTherapies, getAllTherapyAssessments } from 'App/services/AdminService';
import AssessmentService from 'App/services/AssessmentService';
import { errorHandler } from 'App/utils';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import useFetchTherapyCompanyAssessments from 'App/hooks/useFetchTherapyCompanyAssessments';
import useFetchCompanies from 'App/hooks/useFetchCompanies';
import { useUserContext } from 'App/contexts/UserContext';
import { SelectInput } from 'Shared/form/SelectField';

const THERIGY_CO_NAME = 'Therigy';

const isNullOrEmpty = R.anyPass([R.isNil, R.isEmpty]);

function CloneAssessmentPage() {
    //#region State
    const navigate = useNavigate();
    const { categoryId, assessmentId } = useParams();
    const user = useUserContext();
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [assessments, setAssessments] = useState([]);
    const [sourceAssessment, setSourceAssessment] = useState({});
    const [category, setCategory] = useState(null);
    const [targetAssessment, setTargetAssessment] = useState(null);
    const [isAssessmentsLoading, setIsAssessmentsLoading] = useState(false);

    const [targetCompanyAssessment, setTargetCompanyAssessment] = useState(null);

    const hasSuperCloneAssessment = useIsPermitted(PERMISSION_TYPES.ENABLE_SUPER_CLONE_ASSESSMENT);

    const isClinicalAdminCompany = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);

    const { data: allCompaniesData, isLoading: isCompaniesLoading } = useFetchCompanies({
        enabled: hasSuperCloneAssessment,
    });

    const allCompanies = useMemo(() => {
        return R.values(allCompaniesData);
    }, [allCompaniesData]);

    const userCompanyID = useMemo(() => {
        if (isClinicalAdminCompany) {
            return user.active_company.ID;
        }
    }, [isClinicalAdminCompany, user.active_company.ID]);

    const defaultOption = useMemo(() => {
        if (!allCompanies || !userCompanyID) return null;
        return allCompanies.find((c) => c?.id === userCompanyID) || null;
    }, [allCompanies, userCompanyID]);

    const [targetCompany, setTargetCompany] = useState(() => defaultOption);

    const { data: companyAssessmentsData, isLoading: isCompanyAssessmentsLoading } = useFetchTherapyCompanyAssessments(
        category?.id,
        targetCompany?.id
    );

    const companyAssessments = useMemo(() => {
        return R.values(companyAssessmentsData);
    }, [companyAssessmentsData]);

    //#endregion

    //#region FetchData
    useEffect(() => {
        if (categoryId && assessmentId) {
            const promise = [getAllTherapies(), AssessmentService.getAssessment(assessmentId)];
            Promise.all(promise)
                .then(([therapyCategories, currentAssessment]) => {
                    //prettier-ignore
                    R.compose(
                        setCategories,
                        R.sortBy(R.compose(R.toLower, R.prop('name'))),
                        R.values,
                    )(therapyCategories);

                    setSourceAssessment(currentAssessment);
                })
                .catch(errorHandler)
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [categoryId, assessmentId]);

    const targetCategoryId = R.prop('id', category);

    useEffect(() => {
        if (targetCategoryId && assessmentId && (!hasSuperCloneAssessment || targetCompany?.id === userCompanyID)) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setTargetAssessment(null);
            setAssessments([]);
            setIsAssessmentsLoading(true);
            getAllTherapyAssessments(targetCategoryId)
                .then(R.values)
                .then(R.reject(R.propEq(assessmentId, 'id')))
                .then(setAssessments)
                .finally(() => {
                    setIsAssessmentsLoading(false);
                });
        }
    }, [targetCategoryId, assessmentId, hasSuperCloneAssessment, targetCompany, userCompanyID]);

    //#endregion

    //#region Click/Action/Event Handlers
    const navigateBack = () => navigate(generatePath(assessmentManagerHomeRoute, { categoryId }));

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);
        const isNotClinicalAdminCompany = hasSuperCloneAssessment && targetCompany?.id !== userCompanyID;
        const targetAssessmentID = isNotClinicalAdminCompany ? targetCompanyAssessment?.id : targetAssessment?.id;
        const targetCompanyID = isNotClinicalAdminCompany ? targetCompany?.id : null;
        cloneAssessment(assessmentId, targetAssessmentID, targetCompanyID)
            .then(navigateBack)
            .catch((error) => {
                errorHandler(error);
                setIsLoading(false);
            });
    };

    const isValid = useMemo(() => {
        const assessmentSelected =
            hasSuperCloneAssessment && targetCompany?.id !== userCompanyID ? targetCompanyAssessment : targetAssessment;
        //prettier-ignore
        return R.allPass([
            R.prop('assessmentId'),
            x => Boolean(R.path(['id'], x.assessmentSelected)),
        ])({
            assessmentId,
            assessmentSelected,
        });
    }, [
        assessmentId,
        targetAssessment,
        targetCompanyAssessment,
        hasSuperCloneAssessment,
        targetCompany,
        userCompanyID,
    ]);
    //#endregion

    if (isLoading) {
        return (
            <FlexCenter>
                <TherigyLoadingSpinner />
            </FlexCenter>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <Row>
                        <Col sm={12}>
                            <FormGroup>
                                <FormLabel htmlFor="movingAssessment">Cloning assessment</FormLabel>
                                <FormControl
                                    id="movingAssessment"
                                    type="text"
                                    autoComplete="off"
                                    disabled
                                    value={sourceAssessment?.name}
                                />
                            </FormGroup>
                        </Col>

                        <Col sm={12}>
                            <FormGroup>
                                <FormLabel htmlFor="therapeutic-categories" className="field-required">
                                    Select therapeutic category
                                </FormLabel>
                                <SelectInput
                                    options={categories}
                                    getOptionValue={R.prop('id')}
                                    getOptionLabel={R.prop('name')}
                                    value={category}
                                    name="therapeutic-categories"
                                    isClearable
                                    placeholder="Select therapeutic category"
                                    onChange={(opt) => {
                                        setCategory(opt);
                                        setTargetAssessment(null);
                                        setTargetCompany(defaultOption);
                                    }}
                                    isLoading={isLoading}
                                />
                            </FormGroup>
                        </Col>

                        <Col sm={12}>
                            {category && hasSuperCloneAssessment ? (
                                <FormGroup>
                                    <FormLabel htmlFor="targetCompany" className="field-required">
                                        Select target company
                                    </FormLabel>
                                    <SelectInput
                                        name="targetCompany"
                                        options={allCompanies}
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        value={targetCompany}
                                        isClearable
                                        label="Select target company"
                                        onChange={(option) => {
                                            setTargetCompany(option);
                                            setTargetCompanyAssessment(null);
                                        }}
                                        isLoading={isCompaniesLoading}
                                        isDisabled={isCompaniesLoading}
                                    />
                                </FormGroup>
                            ) : null}
                        </Col>

                        <Col sm={12}>
                            {category && (!hasSuperCloneAssessment || targetCompany?.id === userCompanyID) ? (
                                <FormGroup>
                                    <FormLabel htmlFor="target-assessment" className="field-required">
                                        Select target assessment
                                    </FormLabel>
                                    <SelectInput
                                        options={assessments}
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={(option) => {
                                            const companyPath = ['company', 'name'];

                                            if (
                                                R.anyPass([
                                                    R.pathEq(THERIGY_CO_NAME, companyPath),
                                                    R.pathSatisfies(isNullOrEmpty, companyPath),
                                                ])(option)
                                            ) {
                                                return R.prop('name', option);
                                            }

                                            return `${R.prop('name', option)} (${R.path(companyPath, option)})`;
                                        }}
                                        value={targetAssessment}
                                        name="target-assessment"
                                        isClearable
                                        placeholder="Select target assessment"
                                        onChange={setTargetAssessment}
                                        isLoading={isAssessmentsLoading}
                                    />
                                </FormGroup>
                            ) : null}
                        </Col>

                        <Col sm={12}>
                            {targetCompany && targetCompany?.id !== userCompanyID && hasSuperCloneAssessment ? (
                                <FormGroup>
                                    <FormLabel htmlFor="targetAssessment" className="field-required">
                                        Select target assessment
                                    </FormLabel>
                                    <SelectInput
                                        name="targetAssessment"
                                        options={companyAssessments}
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={(option) => {
                                            const companyPath = ['company', 'name'];

                                            if (
                                                R.anyPass([
                                                    R.pathEq(THERIGY_CO_NAME, companyPath),
                                                    R.pathSatisfies(isNullOrEmpty, companyPath),
                                                ])(option)
                                            ) {
                                                return R.prop('name', option);
                                            }

                                            return `${R.prop('name', option)} (${R.path(companyPath, option)})`;
                                        }}
                                        value={targetCompanyAssessment}
                                        isClearable
                                        label="Select target assessment"
                                        onChange={setTargetCompanyAssessment}
                                        isLoading={isCompanyAssessmentsLoading}
                                    />
                                </FormGroup>
                            ) : null}
                        </Col>
                    </Row>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Row>
                        <Col md={12}>
                            <div className="form-button-group">
                                <Button variant="outline-secondary" type="button" onClick={navigateBack}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="primary" disabled={!isValid}>
                                    Clone Assessment
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </form>
    );
}

export default CloneAssessmentPage;
